import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { attachPaymentMethod as actionsAttachPaymentMethod } from 'containers/Billing/actions';
import { useStripe, useElements, PaymentElement, AddressElement } from '@stripe/react-stripe-js';
import clsx from 'clsx';
import { trial } from 'apollo/billing.query';

const PaymentForm = ({ onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const { attachPaymentMethod } = bindActionCreators({
    attachPaymentMethod: actionsAttachPaymentMethod,
  }, dispatch);

  const optionsPaymentElement = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: false,
    },
  };

  const optionsAddressElement = {
    mode: 'billing',
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    setError(null);

    if (!stripe || !elements) {
      setError('Payment system is not loaded. Please refresh the page and try again.');
      setIsProcessing(false);
      return;
    }

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setIsProcessing(false);
        return;
      }

      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/app/v2/account-setup/step/8`,
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message);
        setIsProcessing(false);
        return;
      }

      const paymentMethod = result.setupIntent.payment_method;
      if (paymentMethod) {
        // Attach payment method to customer
        await attachPaymentMethod(paymentMethod);

        // Create trial subscription and wait for it to complete
        const trialResult = await trial();

        // Wait a moment for the subscription to be fully created in Stripe
        await new Promise(resolve => setTimeout(resolve, 2000));

        onPaymentSuccess();
      }
    } catch (err) {
      const errorMessage = err.message || 'Payment failed. Please check your information and try again.';
      setError(errorMessage);
      onPaymentError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="onbaording-payment-form-container">
      <div className="payment-fields">
        <AddressElement options={optionsAddressElement} />
        <PaymentElement options={optionsPaymentElement} />
      </div>

      {error && (
        <div className="payment-error">
          <strong>Payment Error:</strong> {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="onboarding-payment-form"
      >
        <button
          className={clsx('button', 'payment-button', {
            disabled: isProcessing,
            loading: isProcessing,
          })}
          type="submit"
          disabled={isProcessing}
        >
          Get Started
        </button>
      </form>
    </div>
  );
};

export default PaymentForm;
